/**
 * This component is a resolver. It is meant to be used by page templates using
 * data from a CMS.
 *
 * You can send the data you receive from the CMS to the props of this components
 * by spreading them (like <Form {...cmsData} />)
 * and then make these props fit the final module.
 *
 * Note that propTypes are not needed in this component: the GraphQL schema
 * definition is sufficient for that matter.
 */
import React from "react"
import { graphql, navigate } from "gatsby"
import { Section, Loader, Button } from "@bw/bits"
import { useTranslation } from "react-i18next"
import * as yup from "yup"
import { Formik, Form as FormWrap } from "formik"
import { toast } from "react-toastify"
import {
  displayField,
  getVisibleFields,
  postFormFields,
  getFormFields,
  getInitialValues,
  fieldsSchema,
} from "./form-actions"
import styled from "styled-components"

const Form = ({
  suptitle,
  title,
  subtitle,
  button,
  buttonPosition,
  idAttribute,
  classAttribute,
  backgroundVariant,
  form,
  redirection,
  isFollowedByModuleWithBackgroundVariant,
}) => {
  const [formIsSent, setFormIsSent] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(true)
  const [isSuccess, setIsSuccess] = React.useState(false)
  const [fields, setFields] = React.useState(null)
  const { t } = useTranslation()

  React.useEffect(() => {
    getFormFields(form.handle)
      .then(fields => {
        setFields(fields)
        setIsSuccess(true)
      })
      .catch(e => {
        setIsSuccess(false)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [form.handle])

  if (!isLoading && !isSuccess) {
    return null
  }

  return (
    <Section
      {...{
        suptitle,
        title,
        subtitle,
        button,
        buttonPosition,
        idAttribute,
        classAttribute,
        backgroundVariant,
        isFollowedByModuleWithBackgroundVariant,
      }}
    >
      {isLoading && (
        <Loader
          messages={[t("loading"), t("loadingTakesLonger")]}
          treshold={5}
        />
      )}

      {isSuccess && formIsSent === false && (
        <Formik
          initialValues={getInitialValues(fields)}
          validationSchema={yup.object().shape({
            ...fieldsSchema(fields, t),
          })}
          onSubmit={async values => {
            return postFormFields(form.handle, values)
              .then(() => {
                if (redirection?.url) {
                  navigate(redirection.url)
                } else {
                  setFormIsSent(true)
                }
              })
              .catch(() => {
                toast.error(t("form.sentFail"))
              })
          }}
        >
          {formik => (
            <FormWrap
              style={{
                maxWidth: Object.values(fields)
                  .map(field => field.width)
                  .some(w => w !== 100)
                  ? "640px"
                  : "480px",
              }}
            >
              <FieldList>
                {getVisibleFields(Object.values(fields), formik).map(field => (
                  <FieldWidth key={field.handle} percent={field.width}>
                    {displayField(field, formik)}
                  </FieldWidth>
                ))}
              </FieldList>
              <Button
                submit
                label={t("form.buttonSubmit")}
                primary
                disabled={formik.isSubmitting}
              />
            </FormWrap>
          )}
        </Formik>
      )}
      {formIsSent && <div>{t("form.sentSuccess")}</div>}
    </Section>
  )
}

/**
 * This generates a GraphQL Fragment that can be used in a page query:
 *
 * Example:
 * ... on FormModule {
 *   ...FormModuleFragment
 * }
 *
 * This is also what you will get in the props of your Form
 * resolver component.
 */
export const query = graphql`
  fragment FormModuleFragment on FormModule {
    form {
      api_url
      handle
    }
    redirection {
      url
    }
  }
`

export { Form }

const FieldWidth = styled.div`
  @media (min-width: 768px) {
    flex: 0 0 calc(${props => props.percent || 100}% - 20px);
  }
`

const FieldList = styled.div`
  @media (min-width: 768px) {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    column-gap: 20px;
  }
`
